// Slide-out nav drawer — opens from the header hamburger. Content is the
// user's Sidebar Configuration (same Settings → Sidebar list as web),
// filtered to visible items in their configured order.
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Modal, Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { ChevronDown, ChevronRight } from 'lucide-react-native';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii } from '../v10/theme';
import { useSidebarConfig, resolveSidebarDest, type SidebarItem } from './useSidebarConfig';
import { iconForDest, iconForTool } from './toolIcons';
import { SIDEBAR_GEAR_GROUPS, SIDEBAR_PINNED_TOOL_IDS, sidebarRankLabel } from './sidebarGearGroups';
import { GlassFill } from './glass';

const DRAWER_WIDTH_RATIO = 0.78;

export function NavDrawer({
  open,
  userId,
  onClose,
  onOpenTool,
  onOpenHome,
  onOpenSettings,
  onOpenAllTools,
}: {
  open: boolean;
  userId: string | undefined;
  onClose: () => void;
  onOpenTool: (toolId: string) => void;
  onOpenHome: () => void;
  onOpenSettings: () => void;
  onOpenAllTools: () => void;
}) {
  const { width } = useWindowDimensions();
  const { t } = useTheme();
  const styles = useStyles();
  const drawerWidth = Math.min(320, width * DRAWER_WIDTH_RATIO);
  const translateX = useRef(new Animated.Value(-drawerWidth)).current;
  const backdropOpacity = useRef(new Animated.Value(0)).current;
  // Keep the Modal mounted through the close animation so the panel slides
  // out (instead of popping) before it unmounts.
  const [rendered, setRendered] = useState(false);
  // Dropdown sections default closed; multiple can be open at once.
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const { visibleItems } = useSidebarConfig(userId);

  useEffect(() => {
    if (open) setRendered(true);
    Animated.parallel([
      Animated.timing(translateX, {
        toValue: open ? 0 : -drawerWidth,
        duration: 260,
        useNativeDriver: true,
      }),
      Animated.timing(backdropOpacity, {
        toValue: open ? 1 : 0,
        duration: 260,
        useNativeDriver: true,
      }),
    ]).start(({ finished }) => {
      if (finished && !open) setRendered(false);
    });
  }, [open, drawerWidth, translateX, backdropOpacity]);

  const handleSelect = (item: (typeof visibleItems)[number]) => {
    const dest = resolveSidebarDest(item);
    if (!dest) return;
    onClose();
    if (dest.kind === 'home') onOpenHome();
    else if (dest.kind === 'settings') onOpenSettings();
    else onOpenTool(dest.toolId);
  };

  // Section item lookup is by deity toolId, not the user-editable Settings →
  // Sidebar groups (those stay a separate freeform feature) — the dropdown
  // sections are the fixed ORDER-gear + SHAPE roster from sidebarGearGroups.
  const itemByToolId = new Map<string, SidebarItem>();
  for (const item of visibleItems) {
    const dest = resolveSidebarDest(item);
    if (dest?.kind === 'tool') itemByToolId.set(dest.toolId, item);
  }
  const groupedToolIds = new Set(SIDEBAR_GEAR_GROUPS.flatMap((g) => g.toolIds));
  const pinned = visibleItems.filter((item) => {
    const dest = resolveSidebarDest(item);
    if (dest?.kind === 'home' || dest?.kind === 'settings') return true;
    return dest?.kind === 'tool' && SIDEBAR_PINNED_TOOL_IDS.includes(dest.toolId);
  });
  // Anything visible that isn't pinned and isn't in a gear/SHAPE section
  // (e.g. a custom item with an unrecognized href) still needs to render.
  const leftover = visibleItems.filter((item) => {
    const dest = resolveSidebarDest(item);
    if (dest?.kind === 'home' || dest?.kind === 'settings') return false;
    if (dest?.kind === 'tool') return !SIDEBAR_PINNED_TOOL_IDS.includes(dest.toolId) && !groupedToolIds.has(dest.toolId);
    return true;
  });
  const sections = SIDEBAR_GEAR_GROUPS.map((group) => ({
    group,
    items: group.toolIds.map((id) => itemByToolId.get(id)).filter((i): i is SidebarItem => !!i),
  })).filter((s) => s.items.length > 0);

  // Check if ORACLE is already in the sidebar; if not, add hardcoded entry (ledger #12).
  const hasOracle = itemByToolId.has('oracle');

  return (
    <Modal visible={rendered} transparent animationType="none" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Animated.View style={[StyleSheet.absoluteFill, styles.scrim, { opacity: backdropOpacity }]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        </Animated.View>
        <Animated.View style={[styles.drawer, { width: drawerWidth, transform: [{ translateX }] }]}>
          <GlassFill strong radius={radii.panel} />
          <ScrollView contentContainerStyle={styles.content}>
            <Text style={styles.title}>Menu</Text>

            {pinned.map((item) => (
              <DrawerRow key={item.id} item={item} onPress={() => handleSelect(item)} />
            ))}

            {sections.map(({ group, items }) => {
              const open = openGroups.has(group.key);
              const Chevron = open ? ChevronDown : ChevronRight;
              return (
                <View key={group.key} style={styles.group}>
                  <Pressable style={styles.groupHeader} onPress={() => toggleGroup(group.key)}>
                    <Text style={styles.groupTitle}>{group.label}</Text>
                    <Chevron size={16} color={t.ivorySoft} strokeWidth={2.5} />
                  </Pressable>
                  {open && (
                    <>
                      {items.map((item) => (
                        <DrawerRow key={item.id} item={item} onPress={() => handleSelect(item)} />
                      ))}
                      {group.key === 'objectives' && !hasOracle && (
                        <Pressable
                          style={styles.row}
                          onPress={() => {
                            onClose();
                            onOpenTool('oracle');
                          }}
                        >
                          {React.createElement(iconForTool('oracle'), {
                            size: 20,
                            color: t.ivorySoft,
                            strokeWidth: 2,
                          })}
                          <Text style={styles.rowText}>ORACLE</Text>
                        </Pressable>
                      )}
                    </>
                  )}
                </View>
              );
            })}

            {leftover.map((item) => (
              <DrawerRow key={item.id} item={item} onPress={() => handleSelect(item)} />
            ))}

            {visibleItems.length === 0 && (
              <Text style={styles.empty}>No items configured. Set them up in Settings → Sidebar.</Text>
            )}

            <Pressable
              style={styles.allToolsRow}
              onPress={() => {
                onClose();
                onOpenAllTools();
              }}
            >
              <Text style={styles.allToolsText}>All tools →</Text>
            </Pressable>
          </ScrollView>
        </Animated.View>
      </View>
    </Modal>
  );
}

function DrawerRow({ item, onPress }: { item: SidebarItem; onPress: () => void }) {
  const { t } = useTheme();
  const styles = useStyles();
  const dest = resolveSidebarDest(item);
  const Icon = iconForDest(dest);
  // Relabel ASTRS to ASTRA (ledger #13) — the spine editor always opens ASTRA orbital view.
  const displayLabel =
    dest?.kind === 'tool' && dest.toolId === 'astrs' && item.label === 'ASTRS'
      ? 'ASTRA'
      : item.label;
  const rank = dest?.kind === 'tool' ? sidebarRankLabel(dest.toolId) : null;
  return (
    <Pressable style={styles.row} onPress={onPress}>
      <Icon size={20} color={t.ivorySoft} strokeWidth={2} />
      <Text style={styles.rowText}>
        {displayLabel}
        {rank && <Text style={styles.rowRank}> - {rank}</Text>}
      </Text>
    </Pressable>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
    backdrop: { flex: 1, flexDirection: 'row', justifyContent: 'flex-start' },
    scrim: { backgroundColor: c.overlay },
    drawer: {
      height: '100%',
      backgroundColor: 'transparent',
      overflow: 'hidden',
      borderTopRightRadius: radii.panel,
      borderBottomRightRadius: radii.panel,
      borderRightWidth: 1,
      borderColor: t.navyLine,
      paddingTop: 60,
      ...neu.raisedLg,
    },
    content: { paddingHorizontal: 18, paddingBottom: 40 },
    title: { fontSize: 13, fontWeight: '800', letterSpacing: 2.5, color: t.ivoryFaint, marginBottom: 12, textTransform: 'uppercase', fontFamily: DISPLAY_FONT },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 14,
      paddingVertical: 13,
    },
    rowText: { fontSize: 16, fontWeight: '700', color: t.ivory, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
    rowRank: { fontSize: 12, fontWeight: '700', color: t.ivoryFaint },
    group: { marginTop: 16 },
    groupHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 6,
      borderBottomWidth: 1,
      borderBottomColor: t.lineFaint,
    },
    groupTitle: { fontSize: 13, fontWeight: '800', color: t.ivorySoft, textTransform: 'uppercase', letterSpacing: 2, fontFamily: DISPLAY_FONT },
    empty: { fontSize: 13, color: t.ivoryFaint, marginTop: 20, lineHeight: 19 },
    allToolsRow: { paddingVertical: 16, marginTop: 8 },
    allToolsText: { fontSize: 15, fontWeight: '800', color: t.goldBright, letterSpacing: 1, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  }),
);
